import { CUSTOM_VIEWS } from "@/pages/home-page/util";
import { DateRangeType } from "@fluentui/react-calendar-compat";
import { View } from "react-big-calendar";
import { create } from "zustand";
import authStore from "../auth";
import { endOfDay, endOfWeek, startOfMonth, endOfMonth, startOfDay, startOfWeek } from "date-fns";
import { LocalStorageKeys } from "@/const/keys";
import { FilterMode } from "@/pages/home-page/components/filter/model";
import { Filter } from "@/services/filter-service/Model";

type State = {
  view: View;
  date: Date;
  navigatedDate: Date;
  dateRangeType: DateRangeType;
  agendaView: DateRangeType.Day | DateRangeType.Week;
  timelineTimeRange: {
    visibleTimeStart: number;
    visibleTimeEnd: number;
  };
  searchTermEvents: string;
  appliedFilter: FilterMode | null;
  filterItems: Filter[];
};

type TimelineTimeRange = {
  visibleTimeStart: number;
  visibleTimeEnd: number;
};

type Action = {
  setView: (view: View) => void;
  setAgendaView: (agendaView: DateRangeType.Day | DateRangeType.Week) => void;
  setNavigatedDate: (date: Date) => void;
  setDate: (date: Date) => void;
  setDateRangeType: (dateRangeType: DateRangeType) => void;

  setTimelineTimeRange: (timelineTimeRange: TimelineTimeRange) => void;
  getInitialTimelineRange: (currentDate: Date) => TimelineTimeRange;
  getTimelineRangeByZoom: (currentDate: Date, zoom: "day" | "week" | "month") => TimelineTimeRange;
  initializeTimelineTimeRange: () => void;
  initializeDefaultSelectedDate: (viewDefault: View) => void;

  saveTimelineRangeToLocalStorage: () => void;
  saveDefaultSelectedDate: () => void;
  clearDefaultSelectedDateFromSessionStorage: () => void;
  clearTimelineTimeRangeFromSessionStorage: () => void;

  onSearchTermEvents: (keyword: string) => void;

  setAppliedFilter: (appliedFilter: FilterMode | null) => void;
  setFilterItems: (filterItems: Filter[]) => void;
};

const calendarStore = create<State & Action>()((set, get) => ({
  view: CUSTOM_VIEWS.DAY,
  agendaView: DateRangeType.Day,
  date: new Date(),
  navigatedDate: new Date(),
  dateRangeType: DateRangeType.Day,
  timelineTimeRange: {
    visibleTimeStart: 0,
    visibleTimeEnd: 0
  },
  appliedFilter: null,
  filterItems: [],

  searchTermEvents: "",

  setFilterItems(filterItems) {
    set({
      filterItems
    });
  },

  onSearchTermEvents(keyword) {
    set({
      searchTermEvents: keyword
    });
  },

  setAppliedFilter(appliedFilter) {
    set({
      appliedFilter
    });
  },

  initializeDefaultSelectedDate: (viewDefault: View) => {
    const defaultSelectedDate = sessionStorage.getItem(LocalStorageKeys.DATE_SELECTED);
    if (!defaultSelectedDate) {
      set({ view: viewDefault });
      return;
    }

    const parsedDefaultSelectedDate = JSON.parse(defaultSelectedDate);
    set({
      date: new Date(parsedDefaultSelectedDate.date),
      view: parsedDefaultSelectedDate.view as View,
      dateRangeType: parsedDefaultSelectedDate.dateRangeType as DateRangeType,
      navigatedDate: new Date(parsedDefaultSelectedDate.navigatedDate),
      agendaView: parsedDefaultSelectedDate.agendaView as DateRangeType.Day | DateRangeType.Week
    });
  },

  setTimelineTimeRange: (timelineTimeRange: TimelineTimeRange) => {
    set({ timelineTimeRange });
    get().saveTimelineRangeToLocalStorage();
  },

  initializeTimelineTimeRange: () => {
    const localStorageTimelineTimeRange = sessionStorage.getItem(
      LocalStorageKeys.TIMELINE_TIME_RANGE
    );

    if (localStorageTimelineTimeRange) {
      const timelineTimeRange = JSON.parse(localStorageTimelineTimeRange);
      const initialTimelineTimeRange = timelineTimeRange as TimelineTimeRange;

      set({ timelineTimeRange: initialTimelineTimeRange });

      return initialTimelineTimeRange;
    }

    const initialTimelineTimeRange = get().getInitialTimelineRange(new Date());
    set({ timelineTimeRange: initialTimelineTimeRange });
  },

  getInitialTimelineRange: (currentDate: Date) => {
    const { user } = authStore.getState();
    const { getTimelineRangeByZoom } = get();

    const defaultZoom = user?.setting?.defaultZoomInTimeLine?.toLowerCase() || "day";
    return getTimelineRangeByZoom(currentDate, defaultZoom as "day" | "week" | "month");
  },

  getTimelineRangeByZoom: (currentDate: Date, zoom: "day" | "week" | "month") => {
    switch (zoom) {
      case "week": {
        const start = startOfWeek(currentDate, { weekStartsOn: 0 });
        const end = endOfWeek(currentDate, { weekStartsOn: 0 });
        return {
          visibleTimeStart: start.getTime(),
          visibleTimeEnd: end.getTime()
        };
      }
      case "month": {
        const start = startOfMonth(currentDate);
        const end = endOfMonth(currentDate);
        return {
          visibleTimeStart: start.getTime(),
          visibleTimeEnd: end.getTime()
        };
      }
      case "day":
      default: {
        const start = startOfDay(currentDate);
        const end = endOfDay(currentDate);
        return {
          visibleTimeStart: start.getTime(),
          visibleTimeEnd: end.getTime()
        };
      }
    }
  },

  setNavigatedDate: (date: Date) => {
    set({ navigatedDate: date });
    get().saveDefaultSelectedDate();
  },

  setDate: (date: Date) => {
    set({ date, navigatedDate: date });
    get().saveDefaultSelectedDate();
  },

  setDateRangeType: (dateRangeType: DateRangeType) => {
    set({ dateRangeType });
    get().saveDefaultSelectedDate();
  },

  setAgendaView: (agendaView: DateRangeType.Day | DateRangeType.Week) => {
    set({ agendaView });
    get().saveDefaultSelectedDate();
  },

  setView: (view: View) => {
    set({ view });

    switch (view) {
      case CUSTOM_VIEWS.MONTH:
        set({ dateRangeType: DateRangeType.Month });
        break;
      case CUSTOM_VIEWS.WEEK:
        set({ dateRangeType: DateRangeType.Week });
        break;
      case CUSTOM_VIEWS.WORK_WEEK:
        set({ dateRangeType: DateRangeType.WorkWeek });
        break;
      default:
        set({ dateRangeType: DateRangeType.Day });
        set({ agendaView: DateRangeType.Day });
        break;
    }
    get().saveDefaultSelectedDate();
  },

  saveDefaultSelectedDate: () => {
    const { date, view, dateRangeType, navigatedDate, agendaView } = get();
    sessionStorage.setItem(
      LocalStorageKeys.DATE_SELECTED,
      JSON.stringify({
        date,
        view,
        dateRangeType,
        navigatedDate,
        agendaView
      })
    );
  },

  saveTimelineRangeToLocalStorage: () => {
    const { timelineTimeRange } = get();
    sessionStorage.setItem(LocalStorageKeys.TIMELINE_TIME_RANGE, JSON.stringify(timelineTimeRange));
  },

  clearDefaultSelectedDateFromSessionStorage: () => {
    sessionStorage.removeItem(LocalStorageKeys.DATE_SELECTED);
    set({
      date: new Date(),
      view: CUSTOM_VIEWS.DAY,
      dateRangeType: DateRangeType.Day,
      navigatedDate: new Date(),
      agendaView: DateRangeType.Day,
      searchTermEvents: ""
    });
  },

  clearTimelineTimeRangeFromSessionStorage: () => {
    sessionStorage.removeItem(LocalStorageKeys.TIMELINE_TIME_RANGE);
  }
}));

export default calendarStore;
